import React from 'react'
import { arrayMove } from 'react-sortable-hoc'
import Toast from 'blocks.simple/toast/toast'
import { toast } from 'react-toastify'
import catalogTemplate from './__template'
import { api } from 'core/api/ConnectionManager'
import deepEqual from 'fast-deep-equal'
import merge from 'merge'
import helpers, { IRecordWithStrings } from 'core/helpers'
import { createCachedDebounce } from 'core/utils'
import translate from 'core/translate'
import template from 'es6-template-strings'
import moment from 'moment'
import { isExist, isNotEmptyArray } from '../../core/utils/coreUtil'
import { getURLSearchParamsByLocation, getURLSearchParamsString } from 'features/routes/utils'
import { changeFilter, changeLocation, history } from 'features/routes'
import { isHorizontalMenu } from '../../core/helpers/menu'
import { RouteComponentProps } from 'react-router'
import store from 'core/store'
import { ItemData } from '../../core/models/Template'
import { IUser } from '../../core/models/Users'
import type { ToastId } from 'blocks.simple/toast/toast.types'
import type { ApiEventsType } from 'core/api/events'
import type { IBreadcrumbsItem } from 'core/models/Breadcrumbs'
import type { IActionToPermissionMap } from '../../organisms/TreeViewCatalog/TreeViewCatalogList/TreeViewCatalogList-types'
import type { CatalogCustomData, CatalogDraft, CatalogField, CatalogItem, CatalogListTemplate } from './catalog.types'
import type { AdvancedSearchQuery } from 'core/models/AdvancedSearch'

let interval: ReturnType<typeof setInterval> | null = null

type CatalogChangeItem = ItemData | ItemData[]
type CatalogObserver = (callback: (dataItem: ItemData) => void, clearList: string[]) => void
type CatalogMultipleDeleteObserver = (callback: (ids: number[]) => void, clearList: string[]) => void
type CatalogMultipleUpdateObserver = (callback: (items: ItemData[]) => void, clearList: string[]) => void
type CatalogSort = Array<[string, 'ASC' | 'DESC']>
type CatalogComponent = React.ComponentType<Record<string, unknown>>

interface ICatalogResponse {
    data: ItemData[]
    nextPageToken?: string
    total?: number
    lastPage?: number | null
}

interface ICatalogGetOptions {
    props?: ICatalogMethodsProps
    advancedSearchSettings?: AdvancedSearchQuery
    filter?: boolean | Record<string, unknown>
}

interface ICatalogTemplate extends CatalogListTemplate {
    folderName: string
    getMethod: ApiEventsType
    getMethodParams?: Record<string, unknown>
    customParams?: Record<string, unknown>
    getByFiltersMethod?: ApiEventsType
    getByFiltersParams?: Record<string, unknown>
    isDraft?: boolean
    perPage?: number
    withoutFolder?: boolean
    useNewFilter?: boolean
    disableDrag?: boolean
    onCreateListeners: CatalogObserver[]
    onUpdateListeners: CatalogObserver[]
    onDeleteListeners: CatalogObserver[]
    onAdvertisementDeleteListeners?: CatalogObserver[]
    onMultipleDeleteListeners?: CatalogMultipleDeleteObserver[]
    onMultipleUpdateListeners?: CatalogMultipleUpdateObserver[]
    init?: (callback: () => void) => void
    unmount?: () => void
    getBreadcrumbs?: (folderId: unknown) => void
    moveOnUpdate?: (change: { prev: ItemData; next: ItemData }) => boolean
    onCreate?: (dataItem: ItemData) => void
    onUpdate?: (dataItem: CatalogChangeItem) => void
    onDelete?: (dataItem: ItemData) => void
    getBackendModel: (dataItem: ItemData) => Promise<ItemData>
    similarTemplateBackendModel?: (dataItem: ItemData, pathname: string) => Promise<ItemData>
    getItemModel: (
        dataItem: ItemData,
        getSelected: (item: ItemData, props?: ICatalogMethodsProps) => boolean,
        deviceBlockStyle?: string
    ) => ItemData
    updateList?: (currentList: ItemData[], updatedList: ItemData[]) => ItemData[]
    getFilterField?: (state: IState, filter: ICatalogFilter, query: IRecordWithStrings, field: CatalogField) => void
    similarTemplate?: (pathname: string, query: IRecordWithStrings) => Pick<ICatalogTemplate, 'getMethod'>
    onSortEndCallback?: (data: { oldIndex: number; newIndex: number; sorted: ItemData[] }) => void
    useCustomScrollX?: boolean
    useScrollOnlyForPath?: (url: string) => boolean
    settingsPrefixes: string
    tile: CatalogComponent
    map?: CatalogComponent
    addItemButton?: (catalog: CatalogMethods, pathname: string) => React.ReactNode
    customEmptyBlock?: () => React.ReactNode
    emptyIcon?: string
    emptySearchText: () => string
    emptyFolderText: (pathname: string, search: string) => string
    emptyDescription?: () => React.ReactNode
    listWrapperClassName?: string | object
    headerFilters?: CatalogComponent
    disableListScroll?: boolean
    showFilter?: boolean
    getDragData?: (params: { item: CatalogItem }) => unknown
    getNewDataMethod?: boolean
}

interface ICatalogFilter extends Record<string, unknown> {
    searchByFilters?: {
        advancedSearch: AdvancedSearchQuery
        query: string
        appliedFilters: unknown[]
    }
    hasActualSchedule?: string | boolean
}

export interface ICatalogMethodsProps extends RouteComponentProps {
    viewTestClass?: string
    type: string
    selectedItems: ItemData[]
    mapSelect: () => void
    mapUnselect: () => void
    onSelectInfo: (data: ItemData | null) => void
    onSelect: (item: ItemData[]) => void
    drag?: boolean
    onChange: (dataItem: CatalogChangeItem, value: string) => void
    toggleDownloadsModal: () => void
    onSelectProgress: (item: ItemData) => void
    onDragStart: (event: React.DragEvent) => void
    onDragEnd: (event: React.DragEvent) => void
    onGetRootFolder: (value: number) => void
    onGetList: (list: ItemData[]) => void
    onGetTags: (tags: Array<{ id: number }>) => void
    selectedInfo: ItemData
    onDoubleClickObject: (item: ItemData) => void
    disableFolderSelection?: boolean
    disableMultiSelect: (item: ItemData) => boolean
    user: IUser
    disposition?: string
    showOnlySelected?: boolean
    hideHeader?: boolean
    searchPlaceholder: string
    searchable: boolean
    disableFilter?: boolean
    isMobile: boolean
    showHeaderButton: boolean
    small: boolean
    viewLocal: boolean
    changeDisposition: () => void
    breadcrumbsClassName: string
    createFolder: () => void
    additionalData?: unknown
    toggleControls: () => void
    filterItems?: Array<{ name: string; fileType: string }>
    filterOptions?: Record<string, unknown>
    hideList?: boolean
    useWindow?: boolean
    disableOuterScrollForList?: boolean
    classNameContent: string
    isHideList?: boolean
    windowWidth: number
    identificatedItems: Array<{ id: number; timeoutS: number }>
    minimize?: boolean
    cols: number
    isMobileDragEnabled?: boolean
    handleSortable?: boolean
    treeView?: boolean
    onEditListItem?: (listItem: ItemData) => void
    allowEmptyList?: boolean
    fixedHeader?: boolean
    wrapperClassName: string
    isExistRightToolbar?: boolean
    tilesWrapperClassName: object
    contentAndFolders?: boolean
    autoSelectContentType?: boolean
    widgetsGallery?: unknown
    customFilterList?: (list: ItemData[]) => ItemData[]
    selectInstancesFromFile?: boolean
    onSelectFromFile?: (file: unknown) => void
    hideView?: boolean
    hideSelection?: boolean
    customData?: CatalogCustomData
    customComponent?: () => React.ReactNode
    onGetItems?: (items: ItemData[]) => void
    selectAllCheckbox?: boolean
    afterLoadAction?: () => void
    customOptions?: {
        useScheduleCatalog?: boolean
        scheduleId?: number
        removedItems?: Array<{ id: number; type?: string }>
        addedItems?: Array<{ id: number; type?: string }>
        [key: string]: unknown
    }
    onToggleCatalogItem?: (data: { selected: boolean; item: ItemData }) => void
    onChangePermissions?: (permissions: IActionToPermissionMap) => void
    permissions?: IActionToPermissionMap
    onlyFolders?: boolean
    disableSticky?: boolean
    onlyWithDoohDspSettings?: boolean
}

export interface IState {
    startFirstQuery: boolean
    nextPageToken: string | null
    isInit: boolean
    downloadList: ItemData[]
    list: ItemData[]
    total: number
    lastPage: number | null
    fields: CatalogField[] | null
    folders: unknown[]
    tags: Array<{ id: number; [key: string]: unknown }>
    breadcrumbs: IBreadcrumbsItem[] | null
    center: { lat: number; lng: number } | null
    isClicked: boolean
    autoMovedToTheFolder: boolean
    filterIsActive: boolean
    dimensions: {
        height: number
    }
    showOnlySelectedItem: ItemData | null
    advancedSearch: AdvancedSearchQuery
    sortForListRequest: CatalogSort | null
    fakeLine: CatalogDraft | null
    scrollHandleWidth: number
    firstLoadIsCompleted: boolean
}

export class CatalogMethods extends React.Component<ICatalogMethodsProps> {
    state: IState
    isShiftPressed: boolean
    loading: boolean
    fullLoading: boolean
    template: ICatalogTemplate
    init: (callback: () => void) => void
    unmount: () => void
    getBreadcrumbs: (folderId: unknown) => void
    getMethodParams: { videoChatImplementedQ: boolean } | {}
    moveOnUpdate: ({ next, prev }: { prev: ItemData; next: ItemData }) => boolean
    onCreate: (dataItem: ItemData) => void
    onUpdate: (dataItem: CatalogChangeItem) => void
    isEqual: (firstItem: { id: number }, secondItem: { id: number }) => boolean
    onDelete: (dataItem: ItemData) => void
    isFolder: (data: CatalogItem) => boolean
    notification: ToastId | null
    query: IRecordWithStrings
    getItemModel: (
        dataItem: ItemData,
        getSelected: (item: ItemData, props?: ICatalogMethodsProps) => boolean,
        deviceBlockStyle?: string
    ) => ItemData
    clearListenersList: string[]
    filter: ICatalogFilter
    tableRef: HTMLTableElement | null
    listRef: HTMLDivElement | null
    scrollFactor = 0
    onlyFolders = false
    listWrapperRef: HTMLDivElement | null = null

    constructor(p_: ICatalogMethodsProps) {
        super(p_)

        this.loading = true
        this.fullLoading = true
        this.state = {
            startFirstQuery: false,
            nextPageToken: '',
            isInit: false,
            downloadList: [],
            list: [],
            total: 0,
            lastPage: null,
            fields: null,
            folders: [],
            tags: [],
            breadcrumbs: null,
            center: null,
            isClicked: false,
            autoMovedToTheFolder: false,
            filterIsActive: false,
            dimensions: {
                height: 73,
            },
            showOnlySelectedItem: null,
            advancedSearch: {},
            sortForListRequest: null,
            fakeLine: null,
            scrollHandleWidth: 0,
            firstLoadIsCompleted: false,
        }
        this.isShiftPressed = false

        this.template = catalogTemplate(p_.type) as ICatalogTemplate

        this.init = this.template.init ? this.template.init.bind(this) : this.baseInit.bind(this)
        this.unmount = this.template.unmount ? this.template.unmount.bind(this) : this.baseUnmount.bind(this)
        this.getBreadcrumbs = this.template.getBreadcrumbs
            ? this.template.getBreadcrumbs.bind(this)
            : this.baseGetBreadcrumbs.bind(this)
        this.get = this.get.bind(this)
        this.getMethodParams = this.template.getMethodParams || {}
        this.getFilter = this.getFilter.bind(this)
        this.onSelectInfo = this.onSelectInfo.bind(this)
        this.onSelectAll = this.onSelectAll.bind(this)
        this.moveOnUpdate = this.template.moveOnUpdate
            ? this.template.moveOnUpdate.bind(this)
            : this.baseMoveOnUpdate.bind(this)
        this.onCreate = this.template.onCreate ? this.template.onCreate.bind(this) : this.baseOnCreate.bind(this)
        this.onUpdate = this.template.onUpdate ? this.template.onUpdate.bind(this) : this.baseOnUpdate.bind(this)
        this.onDelete = this.template.onDelete ? this.template.onDelete.bind(this) : this.baseOnDelete.bind(this)
        this.onSelect = this.onSelect.bind(this)
        this.onSelectCluster = this.onSelectCluster.bind(this)
        this.getSelected = this.getSelected.bind(this)
        this.onSelectProgress = this.onSelectProgress.bind(this)
        this.getModelsList = this.getModelsList.bind(this)
        this.getActiveIndex = this.getActiveIndex.bind(this)
        this.isEqual = this.template.isEqual
        this.isFolder = this.template.isFolder ? this.template.isFolder : () => false
        const query = getURLSearchParamsByLocation(p_.location)
        this.query = helpers.getQuery(query, p_.type)
        this.notification = null
        this.getItemModel = this.template.getItemModel
        this.clearListenersList = []
        this.filter = {}
        this.tableRef = null
        this.listRef = null
    }

    _mounted = true
    _autoExpandedRootFolder = false
    prevGetParams: Record<string, unknown> | null = null
    cache: {
        updated: {
            data: ItemData[]
            debounce: (() => void) | null
        }
    } = {
        updated: {
            data: [],
            debounce: null,
        },
    }

    setAdvancedSearch = (params: AdvancedSearchQuery) => {
        this.setState({ advancedSearch: params })
    }

    getAdvancedSearch = () => {
        return this.state.advancedSearch
    }

    isDraggable(p_: ICatalogMethodsProps, template: Pick<ICatalogTemplate, 'disableDrag' | 'isFolder'>) {
        if (template.disableDrag) {
            return false
        }
        const foldersExists = typeof template.isFolder !== 'undefined'

        return p_.drag || foldersExists
    }

    componentDidMount() {
        const p_ = this.props
        interval = setInterval(() => {
            if (
                p_.location.pathname.includes('/files') ||
                p_.location.pathname.includes('/addAdvanced') ||
                p_.location.pathname.includes('/addContentToDevice') ||
                p_.location.pathname.includes('/broadcasts/edit')
            ) {
                const downloads = store.getState().downloads.downloads
                const downloadsInProgress = (downloads.filter(
                    (download) => download.progress < 100 && download.type === 'file'
                ) as unknown) as ItemData[]
                this.setState({ downloadList: downloadsInProgress })
            }
        }, 1500)

        setTimeout(() => {
            this.init(() => {
                const p_ = this.props

                this.getSelectedItem()

                if (this.isMap(p_)) {
                    p_.mapSelect()
                } else {
                    this.get(false, false, { props: p_ })
                }

                this.template.onCreateListeners.forEach((listener) => {
                    listener((dataItem: ItemData) => {
                        this.get(false, true)
                        this.onCreate(dataItem)
                        p_.onChange(dataItem, 'create')
                    }, this.clearListenersList)
                })
                this.template.onUpdateListeners.forEach((listener) => {
                    listener((dataItem: ItemData) => {
                        createCachedDebounce(
                            dataItem,
                            this.cache.updated,
                            (data) => {
                                this.updateList(data)
                                this.onUpdate(data)
                                p_.onChange(data, 'update')
                            },
                            {
                                mounted: this._mounted,
                            }
                        )
                    }, this.clearListenersList)
                })
                const deleteListeners = (array: CatalogObserver[]) =>
                    array.forEach((listener) => {
                        listener((dataItem: ItemData) => {
                            this.deleteItem(dataItem)
                            this.onDelete(dataItem)
                            p_.onChange(dataItem, 'delete')
                        }, this.clearListenersList)
                    })

                this.template.onMultipleDeleteListeners?.forEach((listener) => {
                    listener((ids: number[]) => {
                        this.multipleDeleteItems(ids)
                    }, this.clearListenersList)
                })

                this.template.onMultipleUpdateListeners?.forEach((listener) => {
                    listener((items) => {
                        this.multipleUpdateItems(items)
                    }, this.clearListenersList)
                })

                if (this.template.onAdvertisementDeleteListeners) {
                    deleteListeners(this.template.onAdvertisementDeleteListeners)
                    deleteListeners(this.template.onDeleteListeners)
                } else {
                    deleteListeners(this.template.onDeleteListeners)
                }
            })
        }, 100)
    }

    componentWillUnmount() {
        this.clearListenersList.forEach((id) => api.removeObserver(id))
        this._mounted = false
        this.unmount()

        this.setState({ downloadList: [] })
        if (interval) {
            clearInterval(interval)
            interval = null
        }
    }

    UNSAFE_componentWillReceiveProps(p_: ICatalogMethodsProps) {
        if (p_.onlyFolders && p_.onlyFolders !== this.onlyFolders) {
            this.onlyFolders = true

            this.setState({ nextPageToken: null, startFirstQuery: false }, () => {
                this.get()
            })
        }
        if (!p_.onlyFolders && this.onlyFolders) {
            this.onlyFolders = false

            this.setState({ nextPageToken: null, startFirstQuery: false }, () => {
                this.get()
            })
        }

        const s_ = this.state
        const query = getURLSearchParamsByLocation(p_.location)

        this.query = helpers.getQuery(query, p_.type)
        if (!this.isMap(p_)) {
            const prevFilter = this.getFilter(this.props)
            const filter = this.getFilter(p_)
            const filterDiff = !deepEqual(prevFilter, filter)

            if (filterDiff) {
                this.get(false, false, { filter: true, props: p_ }, true)
            }
        }

        if (this.isMap(p_) && !this.isMap(this.props)) {
            p_.mapSelect()
            this.setState({
                breadcrumbs: null,
            })
        }

        if (!this.isMap(p_)) {
            p_.mapUnselect()
        }

        const prevDisposition = this.getViewParamsByProps(this.props).disposition
        const nextDisposition = this.getViewParamsByProps(p_).disposition

        if (!this.isMap(p_) && prevDisposition !== nextDisposition) {
            this.get(false, false, { props: p_ }, true)

            if (p_.onSelect) {
                p_.onSelect([])
            }

            p_.onSelectInfo(null)
        }

        if (!deepEqual(this.props.selectedItems, p_.selectedItems)) {
            let updatedList = s_.list.map((listItem, index) => {
                return {
                    ...listItem,
                    __view: {
                        ...listItem.__view,
                        selected: this.getSelected(listItem, p_),
                    },
                }
            })

            this.setState({ list: updatedList })
        }
    }

    onChangeFilterStatus = (statusValue: boolean) => {
        this.setState({ filterIsActive: statusValue })
    }

    onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.shiftKey) {
            this.isShiftPressed = true
        }
    }
    onKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
        if (!e.shiftKey) {
            this.isShiftPressed = false
        }
    }
    getSelectedItem = () => {
        const p_ = this.props
        const deviceBlockStyle = this.getDeviceBlockStyle()

        if (p_.showOnlySelected && p_.selectedInfo) {
            this.template.getBackendModel(p_.selectedInfo).then((response) => {
                const showOnlySelectedItem = this.getItemModel(response, this.getSelected, deviceBlockStyle)

                this.setState({ showOnlySelectedItem })
            })
        }
    }

    baseInit(callback: () => void) {
        this.setState({ isInit: true }, callback)
    }

    baseUnmount() {}

    getSelected(dataItem: ItemData, props?: ICatalogMethodsProps) {
        const p_ = props || this.props
        let selected = false

        if (p_.customOptions?.useScheduleCatalog) {
            const itemType = dataItem.type || 'digitalSignage'
            const removedItems: Array<{ id: number; type?: string }> = p_.customOptions.removedItems || []
            const addedItems: Array<{ id: number; type?: string }> = p_.customOptions.addedItems || []
            const sameItem = (entry: { id: number; type?: string }) =>
                entry.id === dataItem.id && (entry.type || 'digitalSignage') === itemType

            if (removedItems.some(sameItem)) {
                return false
            }
            if (addedItems.some(sameItem)) {
                return true
            }
        }

        if (p_.selectedItems) {
            p_.selectedItems.forEach((selectedItem: ItemData) => {
                if (this.isEqual(dataItem, selectedItem)) {
                    selected = true
                }
            })
        }

        if (!selected && p_.customOptions?.useScheduleCatalog) {
            return Boolean((dataItem as ItemData & { selected?: boolean }).selected)
        }

        return selected
    }

    onSelectByShift = (items: ItemData[] | undefined) => {
        const p_ = this.props
        const { list } = this.state

        if (p_.onSelect && items) {
            const updatedList = list.map((listItem, index) => {
                const findedSelectedItem = items.find((selectedItem) => this.isEqual(selectedItem, listItem))

                return findedSelectedItem
                    ? {
                          ...listItem,
                          __view: {
                              ...list[index].__view,
                              selected: true,
                          },
                      }
                    : listItem
            })

            this.setState({ list: updatedList })

            p_.onSelect(items)
            p_.onSelectInfo(null)
        }
    }

    onResetSelection = () => {
        this.props.onSelect([])
        this.props.onSelectInfo(null)
    }

    onSelect({ selected, item }: { selected: boolean; item: ItemData }) {
        const p_ = this.props
        const s_ = this.state

        let updatedList = s_.list.map((listItem: ItemData) => {
            if (!this.isEqual(item, listItem)) return listItem

            return {
                ...listItem,
                __view: {
                    ...listItem.__view,
                    selected: selected,
                },
            }
        })

        this.setState({ list: updatedList })

        if (p_.customOptions?.useScheduleCatalog && p_.onToggleCatalogItem) {
            p_.onToggleCatalogItem({ selected, item })
            p_.onSelectInfo(null)
            return
        }

        if (p_.onSelect) {
            let selectedItems: ItemData[] = []

            if (selected) {
                selectedItems = [...p_.selectedItems, item]
            } else {
                selectedItems = p_.selectedItems.filter((listItem: ItemData) => !this.isEqual(item, listItem))
            }

            p_.onSelect(selectedItems)
            p_.onSelectInfo(null)
        }
    }

    onFolderOpening = (listItem: CatalogItem, afterCreateAction?: boolean) => {
        const s_ = this.state
        const p_ = this.props
        const folderKey = this.template.folderName ? this.template.folderName : helpers.getNestedFolderKey(p_.type)

        if (this.isTreeLoaded(listItem) && !afterCreateAction) {
            return
        }

        let getMethodParams = this.template.getMethodParams || {}

        if (this.template.customParams) {
            getMethodParams = this.template.customParams
            getMethodParams.sort = s_.sortForListRequest
        }

        const folderParams: Record<string, unknown> = {
            ...getMethodParams,
            [folderKey]: listItem.id,
        }
        if (p_.customOptions?.useScheduleCatalog) {
            delete folderParams.offset
            delete folderParams.perPage
            folderParams.limit = folderParams.limit || 35
            if (p_.customOptions.scheduleId) {
                folderParams.scheduleId = Number(p_.customOptions.scheduleId)
            }
        }

        api.send(p_.customOptions?.useScheduleCatalog ? 'getScheduleCatalog' : this.template.getMethod, folderParams, {
            hideLoader: false,
        }).then((res: any) => {
            let list = Array.isArray(res?.data) ? [...res.data] : Array.isArray(res) ? [...res] : []

            s_.list.forEach((item: ItemData) => {
                if (this.isEqual(listItem, item)) {
                    item.__view.isLoaded = true
                }

                const existItemIndex = list.findIndex((newItem: ItemData) => this.isEqual(newItem, item))

                if (existItemIndex > -1) {
                    list.splice(existItemIndex, 1)
                }
            })

            list = this.getModelsList(list)
            list = list.map((item: ItemData) => {
                item.__view = {
                    selected: this.getSelected(item),
                    nestedKey: folderKey,
                }

                return item
            })

            s_.list = afterCreateAction ? [...list, ...s_.list] : [...s_.list, ...list]
            s_.total = res.nextPageToken ? s_.list.length + 1 : 0
            s_.nextPageToken = res.nextPageToken

            this.setState(s_)
        })
    }
    isTreeLoaded = (listItem: CatalogItem) => {
        const s_ = this.state

        for (let i = 0; s_.list.length > i; i++) {
            if (this.isEqual(listItem, s_.list[i])) {
                s_.list[i].__view.isOpen = !s_.list[i].__view.isOpen

                this.setState(s_)
                if (s_.list[i].__view.isLoaded) {
                    return true
                }
            }
        }

        return false
    }

    onSelectCluster(selectedItems: ItemData[] | undefined) {
        const p_ = this.props

        if (selectedItems && isExist(selectedItems) && isNotEmptyArray(selectedItems) && this.isShiftPressed) {
            const itemsWithoutDoubles = this.getSelectedWithoutDoublesForArray(selectedItems)
            if (itemsWithoutDoubles) {
                this.onSelectByShift(itemsWithoutDoubles)
            }
            return
        }

        if (p_.onSelect && selectedItems) {
            p_.onSelect(selectedItems)
            this.onSelectInfo(-1)
        }
    }

    getSelectedWithoutDoublesForArray = (newSelection: ItemData[] | undefined) => {
        const p_ = this.props

        if (newSelection) {
            const allItems = [...newSelection, ...p_.selectedItems]
            const idsWithoutDoubles = this._getSelectedIdsWithoutDoublesForArray(p_.selectedItems, newSelection)

            return idsWithoutDoubles
                .map((id) => {
                    for (let i = 0; i < allItems.length; i++) {
                        if (allItems[i].id === id) {
                            return allItems[i]
                        }
                    }
                })
                .filter((item): item is ItemData => Boolean(item))
        }
    }

    _getSelectedIdsWithoutDoublesForArray = (selectedItems: ItemData[], newSelection: ItemData[]) => {
        let selectedItemsIds: number[] = selectedItems.map((selected) => selected.id)
        let newSelectionIds: number[] = newSelection.map((selected) => selected.id)
        return Array.from(new Set([...newSelectionIds, ...selectedItemsIds]))
    }

    onSelectProgress(index: number) {
        const item = this.state.list[index] || null
        const p_ = this.props

        p_.toggleDownloadsModal()
        p_.onSelectProgress(item)
    }

    onDragStart = (event: React.DragEvent) => {
        const p_ = this.props

        p_.onDragStart(event)

        if (!p_.onSelect) {
            return
        }

        const count = p_.selectedItems.length ? p_.selectedItems.length : 1
        const message = template(translate('transfer'), { count })
        const toastOptions = {
            closeOnClick: false,
            closeButton: false,
            autoClose: false,
            pauseOnFocusLoss: false,
            pauseOnHover: false,
            position: 'bottom-center',
        }

        this.notification = Toast.default(message, toastOptions)
    }
    onDragEnd = (event: React.DragEvent) => {
        const p_ = this.props

        if (this.notification !== null) {
            toast.dismiss(this.notification)
        }

        p_.onDragEnd(event)
    }

    baseOnCreate() {}

    baseOnUpdate() {}

    baseOnDelete() {}

    baseGetBreadcrumbs() {}

    getFilter(p_: ICatalogMethodsProps): ICatalogFilter {
        const s_ = this.state
        const locationQuery = getURLSearchParamsByLocation(p_.location)
        const query = helpers.getQuery(locationQuery, p_.type)
        const viewParams = this.getViewParamsByProps(p_)

        if (this.template.useNewFilter) {
            this.setState({ sortForListRequest: this.convertSortParams(viewParams.sort, p_.type) })
        }

        const filter: ICatalogFilter = {
            dateFilter: viewParams.dateFilter,
            sort: this.template.useNewFilter ? this.convertSortParams(viewParams.sort, p_.type) : viewParams.sort,
            query: query.query,
            hasActualSchedule: query.hasActualSchedule,
            withFolders: query.withFolders,
            ...p_.filterOptions,
        }

        if (this.template.folderName) {
            filter[this.template.folderName] = query[this.template.folderName]
        }

        if (this.state.fields && !this.template.useNewFilter) {
            this.state.fields.forEach((field) => {
                if (query[field.id]) {
                    if (this.template.getFilterField) {
                        this.template.getFilterField(s_, filter, query, field)
                    } else {
                        filter[field.id] = query[field.id]
                    }
                }
            })
        }

        if (this.template.useNewFilter) {
            filter.searchByFilters = this.convertFilterParams(query)
        }

        return filter
    }

    parseParams(string: string): unknown[] {
        let result: unknown[] = []

        try {
            result = JSON.parse(string)
        } catch (e) {
            console.log(e)
        }

        return result
    }

    convertFilterParams(queryObj: IRecordWithStrings) {
        const query = queryObj.query ? queryObj.query : ''
        const appliedFilters = queryObj.filterList ? this.parseParams(queryObj.filterList) : []
        let data = {
            query,
            appliedFilters,
            advancedSearch: {},
        }

        if (queryObj.advancedSearch) {
            data.advancedSearch = JSON.parse(queryObj.advancedSearch)
        }

        return data
    }

    convertSortParams(params: string, type: string): CatalogSort {
        let convertedParams: CatalogSort = []

        switch (params) {
            case 'name':
                convertedParams = [['name', 'ASC']]
                break
            case '-name':
                convertedParams = [['name', 'DESC']]
                break
            case 'status':
                convertedParams = [['status', 'DESC']]
                break
            case '-status':
                convertedParams = [['status', 'ASC']]
                break
            case '-createdAt':
                convertedParams = [['createdAt', 'DESC']]
                break
            case '-type':
                convertedParams = [['type', 'DESC']]
                break
            case 'title':
                convertedParams = [['title', 'ASC']]
                break
            default:
                convertedParams = []
        }

        if (!convertedParams.some((element: string[]) => element.includes('type')) && type !== 'draftDevices') {
            convertedParams.push(['type', 'ASC'])
        }

        convertedParams.push(['id', 'ASC'])

        return convertedParams
    }

    checkNewFilterExist(filter: ICatalogFilter) {
        if (filter.searchByFilters) {
            const advSearchIsExist = filter.searchByFilters.advancedSearch
                ? !!filter.searchByFilters.advancedSearch.operator || !!filter.searchByFilters.advancedSearch.separator
                : false
            const queryAndAdSearchIsExist = filter.searchByFilters.query && advSearchIsExist
            return Boolean(
                filter.searchByFilters.appliedFilters.length || filter.searchByFilters.query || queryAndAdSearchIsExist
            )
        }
        return false
    }

    get(isLazyLoad?: boolean, quiteLoading?: boolean, getOptions: ICatalogGetOptions = {}, ignoreToken?: boolean) {
        const { props = this.props, advancedSearchSettings = {}, filter: filterOption } = getOptions
        const s_ = this.state
        const p_ = this.props
        const filter = this.getFilter(props)
        this.filter = filter
        const locationQuery = getURLSearchParamsByLocation(p_.location)
        let getMethod = this.template.similarTemplate
            ? this.template.similarTemplate(p_.location.pathname, locationQuery).getMethod
            : this.template.getMethod
        let perPage = this.template.perPage || 35
        let getMethodParams = this.template.getMethodParams || {}
        let params: Record<string, unknown>

        if (!this.template.customParams && !this.template.getByFiltersParams) {
            delete filter['searchByFilters']
            params = {
                ...getMethodParams,
                ...filter,
                ...(typeof filterOption === 'object' ? filterOption : {}),
                ...advancedSearchSettings,
                offset: isLazyLoad ? s_.list.length : 0,
                perPage,
            }

            if (p_.customOptions) {
                params = {
                    ...params,
                    ...p_.customOptions,
                }
            }
        } else if (this.state.nextPageToken && !ignoreToken) {
            if (this.checkNewFilterExist(filter)) {
                getMethod = this.template.getByFiltersMethod || this.template.getMethod
            }

            params = { nextPageToken: this.state.nextPageToken }
        } else {
            if (this.checkNewFilterExist(filter) && filter.searchByFilters) {
                getMethod = this.template.getByFiltersMethod || this.template.getMethod
                params = { ...(this.template.getByFiltersParams || {}) }
                params.isDraft = this.template.isDraft
                params.appliedFilters = filter.searchByFilters.appliedFilters
                params.advancedSearch = {
                    query: filter.searchByFilters.query,
                    ...filter.searchByFilters.advancedSearch,
                }
            } else {
                params = {}

                if (filter.parentId && this.template.customParams) {
                    params = {
                        ...this.template.customParams,
                        parentId: filter.parentId,
                    }
                }
                if (filter.folderId && this.template.customParams) {
                    params = {
                        ...this.template.customParams,
                        folderId: filter.folderId,
                    }
                }
                if (!((filter.parentId || filter.folderId) && this.template.customParams)) {
                    params = { ...this.template.customParams }
                }
            }
            if (filter.sort) {
                params.sort = filter.sort
            }
            if (p_.onlyWithDoohDspSettings) {
                params.onlyWithDoohDspSettings = true
            }
        }

        if (filter.query && this.template.customParams && this.template.customParams.query !== undefined) {
            params.query = filter.query
        }

        if (this.onlyFolders) {
            params.onlyFolders = true
        } else {
            delete params.onlyFolders
        }

        if (p_.type === 'advertisingCampaigns') {
            params.userDate = moment().format('YYYY-MM-DD HH:mm:ss')
        }
        if (locationQuery.contentId) {
            getMethod = this.template.getByFiltersMethod || this.template.getMethod
            delete params.folderId
            params.appliedFilters = [
                {
                    type: 'select',
                    name: 'sources',
                    options: [
                        {
                            value: locationQuery.contentId,
                            selected: true,
                        },
                    ],
                },
            ]
        }

        if (this.template.withoutFolder) {
            delete params.folderId
        }

        if (p_.customOptions?.useScheduleCatalog) {
            const hasFilters = this.checkNewFilterExist(filter)
            const scheduleId = p_.customOptions.scheduleId ? Number(p_.customOptions.scheduleId) : null

            delete params.useScheduleCatalog
            delete params.removedItems
            delete params.addedItems
            delete params.offset
            delete params.perPage
            delete params.isDraft
            delete params.search

            if (params.nextPageToken) {
                params = { nextPageToken: params.nextPageToken }
            } else {
                params.limit = params.limit || 35
                if (scheduleId) {
                    params.scheduleId = scheduleId
                } else {
                    delete params.scheduleId
                }
            }

            if (hasFilters) {
                if (scheduleId) {
                    getMethod = 'getScheduleCatalogByFilters'
                    delete params.scheduleId
                    if (!params.nextPageToken) {
                        if (!Array.isArray(params.appliedFilters)) {
                            params.appliedFilters = []
                        }

                        const prevAdvanced =
                            params.advancedSearch && typeof params.advancedSearch === 'object'
                                ? (params.advancedSearch as Record<string, unknown>)
                                : {}
                        const searchQuery = String(prevAdvanced.query || filter.searchByFilters?.query || '').trim()
                        params.advancedSearch = {
                            ...prevAdvanced,
                            ...(searchQuery ? { query: searchQuery } : {}),
                        }
                    }
                } else {
                    // Create: обычный get_ds_and_group_by_filters
                    getMethod = this.template.getByFiltersMethod || 'getDisplaysAndGroupsByFilters'
                    delete params.scheduleId
                }
            } else {
                getMethod = 'getScheduleCatalog'
            }
        }

        if (isLazyLoad && deepEqual(params, this.prevGetParams)) {
            return
        }

        if (s_.startFirstQuery && !s_.nextPageToken && !ignoreToken) {
            return
        }

        this.prevGetParams = params
        this.loading = true
        this.fullLoading = !isLazyLoad
        if (quiteLoading) {
            this.loading = false
            this.fullLoading = false
        }

        if (!isLazyLoad) {
            this.getBreadcrumbs(filter[this.template.folderName])
        }

        api.send<Record<string, unknown>, ICatalogResponse>(getMethod, params, { hideLoader: false }).then((res) => {
            const p_ = this.props
            const s_ = this.state
            const rows = p_.customOptions?.useScheduleCatalog
                ? Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res)
                    ? res
                    : []
                : res.data
            let list = this.getModelsList(rows as ItemData[])

            if (!isLazyLoad && list.length === 1) {
                if (!filter[this.template.folderName] && !this._autoExpandedRootFolder) {
                    const listItem = list[0]

                    if (this.template.isFolder(listItem)) {
                        p_.onGetRootFolder(listItem.id)
                        changeFilter({ [this.template.folderName]: listItem.id })

                        this._autoExpandedRootFolder = true
                        //this.get(false, false, { filter: { groupId: listItem.id } })
                        return
                    }
                }
            }

            if (!this._autoExpandedRootFolder) {
                this._autoExpandedRootFolder = true
            }

            if (isLazyLoad) {
                list = [...s_.list, ...list]
            }

            this.fullLoading = false
            this.loading = false

            if (this.isList(p_)) {
                list = list.map((item: ItemData) => {
                    if (!item.__view?.hasOwnProperty('nestedKey')) {
                        item.__view = item.__view || {}
                        item.__view.nestedKey = helpers.getNestedFolderKey(p_.type)
                    }

                    if (!isLazyLoad && !filterOption) {
                        const existItem = s_.list.find((listItem: ItemData) => this.isEqual(listItem, item))

                        if (existItem) {
                            item = merge.recursive(true, item, existItem)
                        }
                    }

                    return item
                })

                if (!isLazyLoad && !filterOption) {
                    const nestedItems = s_.list.filter(
                        (listItem) => !list.find((item: ItemData) => this.isEqual(listItem, item))
                    )

                    list = [...list, ...nestedItems]
                }
            }

            p_.onGetList(list)

            if (res.nextPageToken) {
                if (!this.state.startFirstQuery) {
                    this.setState({
                        startFirstQuery: true,
                    })
                }

                this.setState({
                    nextPageToken: res.nextPageToken,
                })
            } else {
                this.setState({
                    nextPageToken: '',
                })
            }

            this.setState(
                {
                    list,
                    total: res.nextPageToken ? list.length + 1 : res.total ? res.total : 0,
                    lastPage: res.lastPage ?? null,
                    fakeLine: null,
                },
                () => {
                    if (p_.onGetItems) {
                        p_.onGetItems(list)
                    }
                }
            )

            if (p_.afterLoadAction && !s_.firstLoadIsCompleted) {
                p_.afterLoadAction()

                this.setState({
                    firstLoadIsCompleted: true,
                })
            }
        })

        if (p_.afterLoadAction && !s_.firstLoadIsCompleted) {
            p_.afterLoadAction()

            this.setState({
                firstLoadIsCompleted: true,
            })
        }
    }

    getNewData() {
        this.setState({ ...this.state, startFirstQuery: false, nextPageToken: '', list: [] }, this.get)
    }

    getModelsList(list: ItemData[]) {
        const deviceBlockStyle = this.getDeviceBlockStyle()

        if (this.props.type === 'files') {
            return [
                // ...this.state.downloadList,
                ...list.map((item) => {
                    return this.getItemModel(item, this.getSelected, deviceBlockStyle)
                }),
            ]
        }

        return list.map((item) => {
            return this.getItemModel(item, this.getSelected, deviceBlockStyle)
        })
    }

    baseMoveOnUpdate() {
        return false
    }

    updateList = (updatedList: ItemData[]) => {
        const s_ = this.state
        const p_ = this.props
        updatedList = this.getModelsList(updatedList)
        const updateItems: ItemData[] = []
        const nextPrevItems: {
            prev: ItemData
            next: ItemData
        }[] = []

        const list = this.template.updateList
            ? this.template.updateList(s_.list, updatedList)
            : s_.list.map((listItem: ItemData) => {
                  const updatedItem = updatedList.find((updatedItem: ItemData) => this.isEqual(updatedItem, listItem))
                  const clonedUpdatedItem: ItemData = helpers.deepCopy(listItem)

                  if (updatedItem) {
                      const item = merge.recursive(true, listItem, updatedItem)

                      updateItems.push(item)
                      nextPrevItems.push({
                          prev: clonedUpdatedItem,
                          next: item,
                      })

                      return item
                  }

                  return listItem
              })

        this.setState({ list, isClicked: false })

        if (p_.selectedInfo) {
            const updatedSelectedInfoItem = updateItems.find((updatedItem) =>
                this.isEqual(p_.selectedInfo, updatedItem)
            )
            if (updatedSelectedInfoItem) {
                p_.onSelectInfo(updatedSelectedInfoItem)

                if (s_.isClicked) {
                    p_.onDoubleClickObject(updatedSelectedInfoItem)
                }
            }
        }

        if (p_.selectedItems && p_.selectedItems.length) {
            const selectedItems = p_.selectedItems.map((selectedItem: ItemData) => {
                const updatedItem = updateItems.find((updateItem) => this.isEqual(selectedItem, updateItem))

                if (updatedItem) {
                    return updatedItem
                }

                return selectedItem
            })

            p_.onSelect(selectedItems)
        }

        if (!this.loading && !this.query.query) {
            nextPrevItems.forEach((updateItem) => {
                if (this.moveOnUpdate(updateItem)) {
                    this.deleteItem(updateItem.next, false)
                }
            })
        }
    }

    deleteItem(dataItem: ItemData, saveSelected?: boolean) {
        const s_ = this.state
        const p_ = this.props
        const list = s_.list.filter((listItem) => !this.isEqual(listItem, dataItem))
        let total = s_.total

        if (s_.total && s_.list.length >= s_.total) {
            total = list.length
        }

        p_.onGetList(list)
        this.setState({ list, total })

        if (!saveSelected) {
            if (p_.selectedInfo && this.isEqual(p_.selectedInfo, dataItem)) {
                this.onSelectInfo(-1)
            }
            if (p_.selectedItems) {
                let selectedItems = [...p_.selectedItems]

                selectedItems = selectedItems.filter((listItem) => !this.isEqual(listItem, dataItem))
                p_.onSelect(selectedItems)
            }
        }
    }

    multipleDeleteItems(ids: number[]) {
        const s_ = this.state
        const p_ = this.props

        let updatedList = [...s_.list]
        let updatedSelectedItems = [...(p_.selectedItems || [])]

        ids.forEach((id) => {
            updatedList = updatedList.filter((listItem) => listItem.id !== id)

            if (p_.selectedInfo && p_.selectedInfo.id === id) {
                this.onSelectInfo(-1)
            }

            updatedSelectedItems = updatedSelectedItems.filter((selectedItem) => selectedItem.id !== id)
        })

        const total = s_.total && s_.list.length >= s_.total ? updatedList.length : s_.total

        p_.onGetList(updatedList)
        p_.onSelect(updatedSelectedItems)
        this.setState({ list: updatedList, total })
    }

    multipleUpdateItems(items: ItemData[]) {
        const s_ = this.state
        const p_ = this.props

        let updatedList = s_.list.map((element) => {
            const updatedItem = items.find((item) => item.id === element.id)

            if (updatedItem) {
                return updatedItem
            }

            return element
        })

        this.setState({ list: updatedList })
    }

    onSelectInfo(index: number | null, disposition?: string) {
        if (typeof index === 'number') {
            const selectedIndex = index - this.state.downloadList.length
            const item = this.state.list[selectedIndex] || null
            const p_ = this.props

            if (item && this.isShiftPressed) {
                this.onSelectSelectInfoByShift(selectedIndex)
                return
            }
            if (item && p_.customOptions?.useScheduleCatalog) {
                if (this.isFolder(item)) {
                    p_.onSelectInfo?.(null)
                    changeFilter({
                        [helpers.prefixQuery({ name: p_.type, field: 'query' })]: undefined,
                        [this.template.folderName]: item.id,
                    })
                }
                return
            }

            if (disposition === 'list' && p_.type === 'files') {
                const query = getURLSearchParamsByLocation(p_.location)
                let search = {}

                if (item.isDirectory) {
                    search = getURLSearchParamsString({
                        ...query,
                        selectedFolder: item.id,
                    })
                } else {
                    if (query.selectedFolder) {
                        delete query.selectedFolder
                    }

                    search = getURLSearchParamsString({
                        ...query,
                    })
                }

                changeLocation({
                    location: p_.location,
                    search,
                })
            }

            p_.onSelectInfo && p_.onSelectInfo(item)
            if (item && this.template.getBackendModel) {
                if (this.template.similarTemplateBackendModel) {
                    this.template
                        .similarTemplateBackendModel(item, p_.location.pathname)
                        .then((itemModel: ItemData) => this.updateList([itemModel]))
                } else {
                    this.template.getBackendModel(item).then((itemModel: ItemData) => this.updateList([itemModel]))
                }
            }
        }
    }

    onSelectInfoByItem = (item: ItemData) => {
        const p_ = this.props

        if (item && this.isShiftPressed) {
            let selectedWithoutDoubles = p_.selectedItems.filter((selected: { id: number }) => selected.id !== item.id)
            selectedWithoutDoubles = [item, ...selectedWithoutDoubles]
            this.onSelectByShift(selectedWithoutDoubles)
            return
        }

        p_.onSelectInfo && p_.onSelectInfo(item)
        if (item && this.template.getBackendModel) {
            this.template.getBackendModel(item).then((itemModel: ItemData) => this.updateList([itemModel]))
        }
    }

    onSelectSelectInfoByShift(index: number) {
        const p_ = this.props
        const { list } = this.state

        let selectedItemIndex: number = 0

        if (p_.selectedInfo) {
            selectedItemIndex = list.findIndex((item) => item.id === p_.selectedInfo.id)
        }

        if (p_.selectedItems && p_.selectedItems.length) {
            selectedItemIndex = list.findIndex((item) => item.id === p_.selectedItems[0].id)
        }

        let selectedItems = list.slice(selectedItemIndex, index + 1)

        if (selectedItemIndex > index) {
            selectedItems = list.slice(index, selectedItemIndex + 1).reverse()
        }

        this.onSelectByShift(selectedItems)
    }

    onSelectAll() {
        const s_ = this.state
        const p_ = this.props

        if (p_.customOptions?.useScheduleCatalog && p_.onToggleCatalogItem) {
            const list = s_.list.map((listItem) => {
                if ((p_.disableFolderSelection && this.isFolder(listItem)) || p_.disableMultiSelect(listItem)) {
                    return listItem
                }

                if (!this.getSelected(listItem)) {
                    p_.onToggleCatalogItem?.({ selected: true, item: listItem })
                }

                return {
                    ...listItem,
                    __view: {
                        ...listItem.__view,
                        selected: true,
                    },
                }
            })
            this.setState({ list })
            p_.onSelectInfo && p_.onSelectInfo(null)
            return
        }

        const newSelectedItems = s_.list.filter((listItem) => {
            if ((p_.disableFolderSelection && this.isFolder(listItem)) || p_.disableMultiSelect(listItem)) {
                return false
            }

            const findItem = p_.selectedItems.find((selectedItem: ItemData) => this.isEqual(listItem, selectedItem))

            return !findItem
        })

        if (p_.onSelect) {
            p_.onSelect([...p_.selectedItems, ...newSelectedItems])
        }
        p_.onSelectInfo && p_.onSelectInfo(null)
    }

    getActiveIndex() {
        const s_ = this.state
        const p_ = this.props
        let activeIndex = null

        if (p_.selectedInfo) {
            s_.list.forEach((listItem, index) => {
                if (this.isEqual(listItem, p_.selectedInfo)) {
                    activeIndex = index
                }
            })
        }

        return activeIndex
    }

    onCenterMap = (center: { lat: number; lng: number }) => {
        this.setState({ center })
    }

    getViewParams = () => {
        const { type, user } = this.props

        const prefix = catalogTemplate(type).settingsPrefixes
        return helpers.getViewParamsByUser(prefix, user)
    }

    getViewParamsByProps = (props: ICatalogMethodsProps) => {
        const { type, user } = props

        const prefix = catalogTemplate(type).settingsPrefixes

        return helpers.getViewParamsByUser(prefix, user)
    }

    getDisposition = () => {
        const { disposition } = this.props
        const viewParams = this.getViewParams()

        if (this.props.location.pathname === '/schedule' && isHorizontalMenu() && viewParams.disposition === 'map')
            return 'tile'

        return disposition || viewParams.disposition
    }

    getDispositionByProps = (props: Pick<ICatalogMethodsProps, 'disposition'>) => {
        const { disposition } = props
        const viewParams = this.getViewParams()

        return disposition || viewParams.disposition
    }

    isList = (props: ICatalogMethodsProps) => {
        return this.getDispositionByProps(props) === 'list'
    }

    isMap(props: ICatalogMethodsProps) {
        const p_ = this.props

        const isMap = this.getDispositionByProps(props) === 'map' && p_.location.pathname.includes('/displays')

        if (isHorizontalMenu()) {
            if (props.location.pathname === '/schedule' && isMap) return false
        }

        return isMap
    }

    getDeviceBlockStyle = () => {
        const { user } = this.props

        return user?.settings?.deviceBlockStyle
    }

    onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        const { list } = this.state
        const sorted = arrayMove(list, oldIndex, newIndex)

        this.setState({
            list: [],
        })

        this.setState({
            list: sorted,
        })

        if (this.template.onSortEndCallback) {
            this.template.onSortEndCallback({ oldIndex, newIndex, sorted })
        }
    }

    customScrollXIsAvailable = () => {
        if (this.template.useCustomScrollX) {
            if (this.template.useScrollOnlyForPath) {
                return this.template.useScrollOnlyForPath(window.location.href)
            }

            return true
        }

        return false
    }
}

export default CatalogMethods
